import * as React from "react";
import Header from "~/components/header/Header";
import FollowHeader from "~/components/header/FollowHeader";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/ui/button";
import { ButtonGroup } from "~/components/ui/button-group";
import { Link } from "~/components/ui/link";
import { Picture } from "~/components/ui/picture";
import { imageUrl } from "~/lib/image-url";
const mockupLeft = imageUrl("/images/home/hero-mockup-left.png");
const mockupMiddle = imageUrl("/images/home/hero-mockup-middle.png");
const mockupRight = imageUrl("/images/home/hero-mockup-right.png");
const mockupLeftMobile = imageUrl("/images/home/hero-mockup-left-mobile.png");
const mockupMiddleMobile = imageUrl("/images/home/hero-mockup-middle-mobile.png");
const mockupRightMobile = imageUrl("/images/home/hero-mockup-right-mobile.png");

export default function Hero() {
  const { t } = useTranslation("home");
  const [followVisible, setFollowVisible] = React.useState(false);
  return (
    <section
      className="relative flex max-h-[90dvh] flex-col overflow-hidden sm:h-[90dvh]"
      style={{
        background:
          "linear-gradient(0deg, rgba(31, 105, 242, 0.1) 0%, rgba(255, 255, 255, 0.1) 54.9%), " +
          "linear-gradient(0deg, var(--background, #FFFFFF), var(--background, #FFFFFF))",
      }}
    >
      <Header ariaHidden={followVisible} />
      <FollowHeader onVisibleChange={setFollowVisible} />
      <div className="h-24 shrink-0" />
      <div className="mx-auto flex w-full flex-col items-center gap-6 px-4 text-center min-[1440px]:max-w-7xl sm:gap-8 lg:px-20">
        <div className="flex flex-col items-center gap-3">
          <h1 className="font-display text-text-heading max-w-[327px] text-[32px] leading-[1.15] font-extrabold sm:max-w-none sm:text-[clamp(2rem,4vw,3.5rem)] sm:leading-[1.1]">
            {t("hero.title")}
          </h1>
          <p className="text-text-body-secondary text-md max-w-[301px] leading-[1.1] sm:max-w-[min(628px,90vw)] sm:text-[clamp(1.125rem,2vw,1.5rem)] sm:leading-[1.3]">
            {t("hero.subtitle")}
          </p>
        </div>
        <div className="w-full sm:hidden">
          <ButtonGroup layout="row" className="w-full">
            <Button asChild variant="secondary" size="sm" className="max-w-[190px] min-w-0 flex-1">
              <Link to="/personal-use">{t("hero.cta_consumer")}</Link>
            </Button>
            <Button asChild variant="secondary" size="sm" className="max-w-[190px] min-w-0 flex-1">
              <Link to="/fleet">{t("hero.cta_fleet")}</Link>
            </Button>
          </ButtonGroup>
        </div>
        <div className="hidden sm:block">
          <ButtonGroup layout="row" className="gap-6">
            <Button asChild variant="secondary" size="xl" className="w-[190px]">
              <Link to="/personal-use">{t("hero.cta_consumer")}</Link>
            </Button>
            <Button asChild variant="secondary" size="xl" className="w-[190px]">
              <Link to="/fleet">{t("hero.cta_fleet")}</Link>
            </Button>
          </ButtonGroup>
        </div>
      </div>
      <div className="min-h-[77px] flex-1 sm:min-h-14" />
      <div className="sm:hidden">
        <MockupsSpreadMobile />
      </div>
      <div className="hidden sm:block">
        <MockupsSpreadDesktop />
      </div>
    </section>
  );
}

//=========================================== Components
function MockupsSpreadDesktop() {
  return (
    <div className="w-full px-6">
      <div className="mx-auto flex w-full max-w-[879px] items-end gap-5">
        <div className="min-w-0 flex-[271]">
          <Picture
            src={mockupLeft}
            alt=""
            loading="eager"
            className="h-auto w-full object-contain"
            style={{
              borderTopLeftRadius: "16.4332% 10.3327%",
              borderTopRightRadius: "16.4332% 10.3327%",
              boxShadow: "6.205px 6.8px 6.8px 0px rgba(137,136,136,0.25), -6.205px 6.8px 6.8px 0px rgba(137,136,136,0.25)",
            }}
          />
        </div>
        <div className="min-w-0 flex-[297]">
          <Picture
            src={mockupMiddle}
            alt=""
            loading="eager"
            className="h-auto w-full object-contain"
            style={{
              borderTopLeftRadius: "16.4232% 10.65%",
              borderTopRightRadius: "16.4232% 10.65%",
              boxShadow: "6.86px 6.8px 6.8px 0px rgba(137,136,136,0.25), -6.86px 6.8px 6.8px 0px rgba(137,136,136,0.25)",
            }}
          />
        </div>
        <div className="min-w-0 flex-[271]">
          <Picture
            src={mockupRight}
            alt=""
            loading="eager"
            className="h-auto w-full object-contain"
            style={{
              borderTopLeftRadius: "16.4332% 10.3327%",
              borderTopRightRadius: "16.4332% 10.3327%",
              boxShadow: "6.205px 6.8px 6.8px 0px rgba(137,136,136,0.25), -6.205px 6.8px 6.8px 0px rgba(137,136,136,0.25)",
            }}
          />
        </div>
      </div>
    </div>
  );
}

function MockupsSpreadMobile() {
  return (
    <div className="flex w-full items-end gap-2 px-5">
      <div className="min-w-0 flex-[103]">
        <Picture
          src={mockupLeftMobile}
          alt=""
          loading="eager"
          className="h-auto w-full object-contain"
          style={{
            borderTopLeftRadius: "16.4064% 9.9741%",
            borderTopRightRadius: "16.4064% 9.9741%",
            boxShadow: "2.363px 2.589px 2.589px 0px rgba(137,136,136,0.25), -2.363px 2.589px 2.589px 0px rgba(137,136,136,0.25)",
          }}
        />
      </div>
      <div className="min-w-0 flex-[113]">
        <Picture
          src={mockupMiddleMobile}
          alt=""
          loading="eager"
          className="h-auto w-full object-contain"
          style={{
            borderTopLeftRadius: "16.4065% 10.3178%",
            borderTopRightRadius: "16.4065% 10.3178%",
            boxShadow: "2.613px 2.589px 2.589px 0px rgba(137,136,136,0.25), -2.613px 2.589px 2.589px 0px rgba(137,136,136,0.25)",
          }}
        />
      </div>
      <div className="min-w-0 flex-[103]">
        <Picture
          src={mockupRightMobile}
          alt=""
          loading="eager"
          className="h-auto w-full object-contain"
          style={{
            borderTopLeftRadius: "16.4064% 9.9741%",
            borderTopRightRadius: "16.4064% 9.9741%",
            boxShadow: "2.363px 2.589px 2.589px 0px rgba(137,136,136,0.25), -2.363px 2.589px 2.589px 0px rgba(137,136,136,0.25)",
          }}
        />
      </div>
    </div>
  );
}
